import { Box, Button, Flex, SimpleGrid, Spacer, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../../contexts/Auth";
import { uniqBy } from "../../../core/utils";
import {
  getAllComics,
  getPreviousAllComics,
  getReading,
} from "../../../services/readingOrdersApi";
import {
  getReadingOrder,
  resetReading,
  setOrderPosition,
  setPreviousComics,
} from "../../reading/actions";
import OrderCompleted from "../../reading/components/OrderCompleted";
import ReadingOrderActions from "../../reading/components/ReadingOrderActions";
import ReadingOrderList from "../../reading/components/ReadingOrderList";
import UpNext from "../../reading/components/UpNext";
import BookClubCountdown from "./BookClubCountdown";

const BookClubReading = ({ club }) => {
  if (club.status === "upcoming") {
    return <BookClubCountdown startsAt={club.startsAt} />;
  }

  return <JoinedBookClubReading club={club} />;
};

const JoinedBookClubReading = ({ club }) => {
  const { authData } = useAuth();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const upNextRef = useRef();
  const previousProgressRef = useRef({ readingId: null, progress: undefined });
  const readingId = club.readingId;

  const readingQuery = useQuery(
    ["reading", readingId],
    () => getReading(readingId),
    {
      enabled: !!readingId,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
  const readingData = readingQuery.data?.data;
  const readingOrder = useSelector(
    (state) => state.orders.readings[readingId]?.comics || []
  );
  const orderPosition = useSelector(
    (state) => state.orders.readings[readingId]?.orderPosition
  );

  useEffect(() => {
    const previous = previousProgressRef.current;
    if (
      previous.readingId === readingId &&
      previous.progress !== undefined &&
      readingData?.progress !== undefined &&
      previous.progress !== readingData.progress
    ) {
      queryClient.invalidateQueries("communityBookClub");
    }
    previousProgressRef.current = {
      readingId,
      progress: readingData?.progress,
    };
  }, [queryClient, readingData?.progress, readingId]);

  const readingComicsQuery = useInfiniteQuery(
    ["readingOrder", readingId],
    ({ pageParam = 1 }) => getAllComics(readingId, pageParam),
    {
      enabled: !!readingData,
      keepPreviousData: true,
      staleTime: 3 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      getNextPageParam: (page) => {
        if (page?.meta.currentPage === page?.meta.lastPage) {
          return undefined;
        }
        return page?.meta?.currentPage + 1;
      },
      onSuccess: (responseData) => {
        const comics = responseData.pages.flatMap((page) => page.data || []);
        dispatch(getReadingOrder(readingId, comics));
        dispatch(setOrderPosition(readingId, readingData?.orderPosition));
      },
    }
  );

  const loadPreviousMutation = useMutation(
    ({ from }) => getPreviousAllComics(readingId, from),
    {
      onSuccess: (responseData) => {
        dispatch(setPreviousComics(readingId, responseData.data));
      },
    }
  );

  const upNext = useSelector((state) =>
    state.orders.readings[readingId]?.comics?.find((item) =>
      state.orders.readings[readingId].orderPosition
        ? item.order >= state.orders.readings[readingId].orderPosition &&
          !item.read &&
          !item.readLater &&
          !item.skipped
        : !item.read && !item.readLater && !item.skipped
    )
  );

  const onPressUpNext = useCallback(() => {
    upNextRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const onReset = useCallback(() => {
    dispatch(resetReading(readingId));
    queryClient.invalidateQueries(["reading", readingId]);
    queryClient.invalidateQueries(["readingOrder", readingId]);
  }, [dispatch, queryClient, readingId]);

  if (!readingId) {
    return (
      <Box bg="gray.700" borderRadius="lg" p="6">
        <Text color="gray.400">
          Join the book club to add its reading order to your Reading.
        </Text>
      </Box>
    );
  }

  const canTrack = !!authData && !!club.joined;
  const isLoading = readingQuery.isLoading || readingComicsQuery.isLoading;
  const visibleReadingOrder = uniqBy(readingOrder);

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={2} alignItems="start">
      <Box bg="gray.700" borderRadius="lg">
        {readingData?.progress === 100 ? (
          <OrderCompleted />
        ) : (
          <UpNext
            readingId={readingId}
            upNext={upNext}
            isLoading={isLoading}
            onPress={onPressUpNext}
            disableTracking={!canTrack}
          />
        )}
      </Box>

      <Box bg="gray.700" borderRadius="lg">
        <Box py="3" px="3" borderBottomColor="gray.900" borderBottomWidth="1px">
          <Flex justifyContent="center">
            <Text fontSize="sm" color="gray.400" mt="2" ml="1">
              READING ORDER
            </Text>
            <Spacer />
            <ReadingOrderActions
              readingId={readingId}
              readingOrder={readingData?.readingOrder}
              orderPosition={orderPosition}
              isReadOnly={!canTrack}
              resetOnly
              onReset={onReset}
            />
          </Flex>
        </Box>

        {Array.isArray(visibleReadingOrder) &&
          visibleReadingOrder[0]?.order !== 1 && (
            <Button
              onClick={() =>
                loadPreviousMutation.mutate({
                  from: visibleReadingOrder[0]?.order,
                })
              }
              isLoading={loadPreviousMutation.isLoading}
              disabled={loadPreviousMutation.isLoading}
              loadingText="Loading previous comics"
              w="100%"
              size="sm"
              variant="outline"
            >
              See previous comics
            </Button>
          )}

        <ReadingOrderList
          readingId={readingId}
          upNext={upNext}
          isLoading={isLoading}
          isFetchingNextPage={readingComicsQuery.isFetchingNextPage}
          readingOrderComics={visibleReadingOrder}
          fetchNextPage={readingComicsQuery.fetchNextPage}
          hasNextPage={readingComicsQuery.hasNextPage}
          upNextRef={upNextRef}
          disableTracking={!canTrack}
        />
      </Box>
    </SimpleGrid>
  );
};

export default BookClubReading;
